import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindValue, html, list } from "../lib/bind";
import { bindCheckbox, bindSelect } from "../lib/formBind";
import { form } from "../lib/form";
import { formatMoney } from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { toast } from "../lib/toast";
import { breadcrumb } from "../stores/breadcrumb";
import {
  type ImportProfile,
  type ImportProfileInput,
  type PreviewRow,
  type SignConvention,
  createImportProfile,
  deleteImportProfile,
  getImportProfile,
  listImportProfiles,
  previewImportProfileMapping,
  updateImportProfile,
} from "../services/bank";

const SIGN_CONVENTION_LABELS: Record<SignConvention, string> = {
  negative_is_debit: "Single signed amount column",
  separate_columns: "Separate debit/credit columns",
  type_column: "Amount + direction column",
};

function emptyProfileInput() {
  return {
    name: "",
    bank_hint: "",
    sign_convention: "negative_is_debit" as SignConvention,
    invert_amount: false,
    date_format: "%d/%m/%Y",
    decimal_style: "dot",
    header_rows: "0",
    column_date: "",
    column_description: "",
    column_reference: "",
    column_balance: "",
    column_amount: "",
    column_debit: "",
    column_credit: "",
    column_type: "",
    column_debit_values: "",
  };
}

/** Reverses columnMapFromForm — populates the editor's flat fields from a saved
 * profile's column_map so an existing profile can be edited in place. */
function profileFormValuesFromProfile(p: ImportProfile): ReturnType<typeof emptyProfileInput> {
  const map = p.column_map;
  const str = (key: string): string => {
    const value = map[key];
    return typeof value === "string" ? value : "";
  };
  const debitValues = Array.isArray(map.debit_values) ? map.debit_values.join(", ") : "";
  return {
    name: p.name,
    bank_hint: p.bank_hint ?? "",
    sign_convention: p.sign_convention,
    invert_amount: p.invert_amount,
    date_format: p.date_format,
    decimal_style: p.decimal_style,
    header_rows: String(p.header_rows),
    column_date: str("date"),
    column_description: str("description"),
    column_reference: str("reference"),
    column_balance: str("balance"),
    column_amount: str("amount"),
    column_debit: str("debit"),
    column_credit: str("credit"),
    column_type: str("type"),
    column_debit_values: debitValues,
  };
}

export function bankProfilesListView(): Node {
  const profiles = signal<ImportProfile[]>([]);
  const loadError = signal("");

  function reload(): void {
    listImportProfiles()
      .then((result) => {
        profiles.value = result;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }
  reload();

  async function doDelete(p: ImportProfile): Promise<void> {
    if (!window.confirm(`Delete import profile "${p.name}"?`)) return;
    try {
      await deleteImportProfile(p.id);
      reload();
      toast.success("Profile deleted.");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Failed to delete profile");
    }
  }

  const fragment = html`<div class="view">
    <p class="form-error"></p>

    <div class="card">
      <div class="card-label-row">
        <a href="/bank-profiles/new" data-link class="btn btn-primary">Add profile</a>
      </div>
      <div class="data-table profiles-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Bank hint</th>
              <th>Sign convention</th>
              <th></th>
            </tr>
          </thead>
          <tbody class="profiles-body"></tbody>
        </table>
      </div>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const profilesTable = fragment.querySelector<HTMLTableElement>(".profiles-table table");
  const profilesBody = fragment.querySelector<HTMLTableSectionElement>(".profiles-body");
  if (!errorEl || !profilesTable || !profilesBody) {
    throw new Error("bank profiles list markup missing expected elements");
  }
  bindErrorText(errorEl, loadError);

  list(
    profilesBody,
    profiles,
    (p) => p.id,
    (p) => {
      const row = html`<tr>
        <td><a href="/bank-profiles/${p.id}" data-link>${p.name}</a></td>
        <td>${p.bank_hint ?? "—"}</td>
        <td>${SIGN_CONVENTION_LABELS[p.sign_convention]}${p.invert_amount ? " (flipped)" : ""}</td>
        <td><button class="btn btn-danger profile-delete-btn" type="button">Delete</button></td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("import profile row template produced no element");
      const deleteBtn = row.querySelector<HTMLButtonElement>(".profile-delete-btn");
      deleteBtn?.addEventListener("click", () => {
        void doDelete(p);
      });
      return labelRow(row, profilesTable);
    },
  );

  return fragment;
}

export function bankProfileDetailView(params: Record<string, string>): Node {
  const paramId = params.id ?? "";
  const isNew = paramId === "new";
  const profileId = signal(isNew ? "" : paramId);
  const savedAt = signal("");
  const errorMessage = signal("");

  breadcrumb.value = { label: "Bank profiles", path: "/bank-profiles" };
  currentScope().cleanup(() => {
    breadcrumb.value = null;
  });

  const profileForm = form(emptyProfileInput());

  if (!isNew) {
    getImportProfile(paramId)
      .then((profile) => {
        profileForm.reset(profileFormValuesFromProfile(profile));
        savedAt.value = profile.updated_at;
      })
      .catch((err: unknown) => {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  }

  function columnMapFromForm(): Record<string, unknown> {
    const f = profileForm.fields;
    const map: Record<string, unknown> = {
      date: f.column_date.value,
      description: f.column_description.value,
    };
    if (f.column_reference.value) map.reference = f.column_reference.value;
    if (f.column_balance.value) map.balance = f.column_balance.value;
    if (f.sign_convention.value === "separate_columns") {
      map.debit = f.column_debit.value;
      map.credit = f.column_credit.value;
    } else {
      map.amount = f.column_amount.value;
      if (f.sign_convention.value === "type_column") {
        map.type = f.column_type.value;
        map.debit_values = f.column_debit_values.value
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
    }
    return map;
  }

  function profileInputFromForm(): ImportProfileInput {
    const f = profileForm.fields;
    return {
      name: f.name.value,
      bank_hint: f.bank_hint.value || null,
      column_map: columnMapFromForm(),
      date_format: f.date_format.value || "%Y-%m-%d",
      decimal_style: f.decimal_style.value || "dot",
      header_rows: Number(f.header_rows.value) || 0,
      sign_convention: f.sign_convention.value,
      invert_amount: f.invert_amount.value,
    };
  }

  async function submit(event: Event): Promise<void> {
    event.preventDefault();
    errorMessage.value = "";
    const f = profileForm.fields;
    if (!f.name.value.trim()) {
      errorMessage.value = "Enter a name.";
      return;
    }
    if (!f.column_date.value.trim() || !f.column_description.value.trim()) {
      errorMessage.value = "Date and description columns are required.";
      return;
    }
    try {
      if (isNew) {
        const created = await createImportProfile(profileInputFromForm());
        toast.queueSuccess("Profile created.");
        location.assign(`/bank-profiles/${created.id}`);
        return;
      }
      const updated = await updateImportProfile(profileId.value, {
        ...profileInputFromForm(),
        updated_at: savedAt.value,
      });
      savedAt.value = updated.updated_at;
      toast.success("Profile updated.");
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 412) {
        errorMessage.value = "This record changed elsewhere — reload and retry.";
      } else {
        errorMessage.value = err instanceof ApiRequestError ? err.body.message : "Save failed";
      }
      toast.error(errorMessage.value);
    }
  }

  async function doDelete(): Promise<void> {
    if (!window.confirm("Delete this import profile?")) return;
    try {
      await deleteImportProfile(profileId.value);
      toast.queueSuccess("Profile deleted.");
      location.assign("/bank-profiles");
    } catch (err) {
      toast.error(err instanceof ApiRequestError ? err.body.message : "Failed to delete profile");
    }
  }

  const profilePreviewSample = signal("");
  const profilePreviewRows = signal<PreviewRow[] | null>(null);
  const profilePreviewError = signal("");

  async function previewProfileMapping(): Promise<void> {
    profilePreviewError.value = "";
    const f = profileForm.fields;
    if (!f.column_date.value.trim() || !f.column_description.value.trim()) {
      profilePreviewError.value = "Fill in the date and description columns first.";
      return;
    }
    if (!profilePreviewSample.value.trim()) {
      profilePreviewError.value = "Paste a CSV sample first.";
      return;
    }
    try {
      profilePreviewRows.value = await previewImportProfileMapping(
        profileInputFromForm(),
        profilePreviewSample.value,
      );
    } catch (err) {
      profilePreviewError.value =
        err instanceof ApiRequestError ? err.body.message : "Preview failed";
    }
  }

  const showAmountField = computed(
    () => profileForm.fields.sign_convention.value !== "separate_columns",
  );
  const showSeparateFields = computed(
    () => profileForm.fields.sign_convention.value === "separate_columns",
  );
  const showTypeFields = computed(
    () => profileForm.fields.sign_convention.value === "type_column",
  );

  const fragment = html`<div class="view">
    <div class="page-header"><h1>${isNew ? "New import profile" : "Edit import profile"}</h1></div>
    <p class="form-error"></p>

    <form class="form profile-form" onsubmit="${submit}">
      <div class="form-row">
        <div class="form-field">
          <label for="profile-name">Name</label>
          <input id="profile-name" required />
        </div>
        <div class="form-field">
          <label for="profile-bank-hint">Bank hint</label>
          <input id="profile-bank-hint" placeholder="optional" />
        </div>
        <div class="form-field">
          <label for="profile-sign-convention">Sign convention</label>
          <select id="profile-sign-convention">
            <option value="negative_is_debit">Single signed amount column</option>
            <option value="separate_columns">Separate debit/credit columns</option>
            <option value="type_column">Amount + direction column</option>
          </select>
        </div>
        <div class="form-field checkbox-field">
          <input id="profile-invert-amount" type="checkbox" />
          <label for="profile-invert-amount">Flip amount sign</label>
        </div>
      </div>
      <p class="field-hint profile-invert-hint">
        Use this when the statement's polarity is the mirror of this app's own
        "negative = money out" rule — e.g. some credit-card exports show purchases as
        positive and payments-received as negative.
      </p>
      <div class="form-row">
        <div class="form-field">
          <label for="profile-col-date">Date column</label>
          <input id="profile-col-date" required />
        </div>
        <div class="form-field">
          <label for="profile-col-description">Description column</label>
          <input id="profile-col-description" required />
        </div>
        <div class="form-field">
          <label for="profile-col-reference">Reference column</label>
          <input id="profile-col-reference" placeholder="optional" />
        </div>
        <div class="form-field">
          <label for="profile-col-balance">Balance column</label>
          <input id="profile-col-balance" placeholder="optional" />
        </div>
      </div>
      <div class="form-row profile-amount-row">
        <div class="form-field">
          <label for="profile-col-amount">Amount column</label>
          <input id="profile-col-amount" />
        </div>
      </div>
      <div class="form-row profile-separate-row">
        <div class="form-field">
          <label for="profile-col-debit">Debit column</label>
          <input id="profile-col-debit" />
        </div>
        <div class="form-field">
          <label for="profile-col-credit">Credit column</label>
          <input id="profile-col-credit" />
        </div>
      </div>
      <div class="form-row profile-type-row">
        <div class="form-field">
          <label for="profile-col-type">Direction column</label>
          <input id="profile-col-type" />
        </div>
        <div class="form-field">
          <label for="profile-col-debit-values">Debit values (comma-separated)</label>
          <input id="profile-col-debit-values" placeholder="e.g. debit, deb" />
        </div>
      </div>
      <div class="form-row">
        <div class="form-field">
          <label for="profile-date-format">Date format</label>
          <input id="profile-date-format" placeholder="%d/%m/%Y" />
        </div>
        <div class="form-field">
          <label for="profile-decimal-style">Decimal style</label>
          <select id="profile-decimal-style">
            <option value="dot">1234.56</option>
            <option value="comma">1234,56</option>
          </select>
        </div>
        <div class="form-field">
          <label for="profile-header-rows">Header/preamble rows</label>
          <input id="profile-header-rows" type="number" min="0" />
        </div>
      </div>
      <div class="form-actions">
        <button class="btn btn-primary" type="submit">Save</button>
        <button class="btn btn-danger delete-btn" type="button">Delete</button>
      </div>
    </form>

    <div class="card profile-preview-card">
      <div class="card-label">Live preview</div>
      <div class="form-field">
        <label for="profile-preview-sample">Paste a CSV sample to preview</label>
        <textarea
          id="profile-preview-sample"
          rows="4"
          placeholder="Paste a few lines from a real export, including the header row"
        ></textarea>
      </div>
      <div class="form-actions">
        <button class="btn preview-profile-btn" type="button">Preview</button>
      </div>
      <p class="form-error profile-preview-error"></p>
      <div class="data-table profile-preview-table">
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Balance</th>
              <th>Reference</th>
              <th>Errors</th>
            </tr>
          </thead>
          <tbody class="profile-preview-body"></tbody>
        </table>
      </div>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const nameInput = fragment.querySelector<HTMLInputElement>("#profile-name");
  const bankHintInput = fragment.querySelector<HTMLInputElement>("#profile-bank-hint");
  const signConventionSelect = fragment.querySelector<HTMLSelectElement>("#profile-sign-convention");
  const invertAmountInput = fragment.querySelector<HTMLInputElement>("#profile-invert-amount");
  const colDateInput = fragment.querySelector<HTMLInputElement>("#profile-col-date");
  const colDescriptionInput = fragment.querySelector<HTMLInputElement>("#profile-col-description");
  const colReferenceInput = fragment.querySelector<HTMLInputElement>("#profile-col-reference");
  const colBalanceInput = fragment.querySelector<HTMLInputElement>("#profile-col-balance");
  const colAmountInput = fragment.querySelector<HTMLInputElement>("#profile-col-amount");
  const colDebitInput = fragment.querySelector<HTMLInputElement>("#profile-col-debit");
  const colCreditInput = fragment.querySelector<HTMLInputElement>("#profile-col-credit");
  const colTypeInput = fragment.querySelector<HTMLInputElement>("#profile-col-type");
  const colDebitValuesInput = fragment.querySelector<HTMLInputElement>("#profile-col-debit-values");
  const dateFormatInput = fragment.querySelector<HTMLInputElement>("#profile-date-format");
  const decimalStyleSelect = fragment.querySelector<HTMLSelectElement>("#profile-decimal-style");
  const headerRowsInput = fragment.querySelector<HTMLInputElement>("#profile-header-rows");
  const amountRow = fragment.querySelector<HTMLElement>(".profile-amount-row");
  const separateRow = fragment.querySelector<HTMLElement>(".profile-separate-row");
  const typeRow = fragment.querySelector<HTMLElement>(".profile-type-row");
  const deleteBtn = fragment.querySelector<HTMLButtonElement>(".delete-btn");
  const previewSampleInput = fragment.querySelector<HTMLTextAreaElement>("#profile-preview-sample");
  const previewBtn = fragment.querySelector<HTMLButtonElement>(".preview-profile-btn");
  const previewErrorEl = fragment.querySelector<HTMLParagraphElement>(".profile-preview-error");
  const previewTable = fragment.querySelector<HTMLTableElement>(".profile-preview-table table");
  const previewBody = fragment.querySelector<HTMLTableSectionElement>(".profile-preview-body");

  if (
    !errorEl ||
    !nameInput ||
    !bankHintInput ||
    !signConventionSelect ||
    !invertAmountInput ||
    !colDateInput ||
    !colDescriptionInput ||
    !colReferenceInput ||
    !colBalanceInput ||
    !colAmountInput ||
    !colDebitInput ||
    !colCreditInput ||
    !colTypeInput ||
    !colDebitValuesInput ||
    !dateFormatInput ||
    !decimalStyleSelect ||
    !headerRowsInput ||
    !amountRow ||
    !separateRow ||
    !typeRow ||
    !deleteBtn ||
    !previewSampleInput ||
    !previewBtn ||
    !previewErrorEl ||
    !previewTable ||
    !previewBody
  ) {
    throw new Error("bank profile detail markup missing expected elements");
  }
  if (isNew) deleteBtn.style.display = "none";

  bindErrorText(errorEl, errorMessage);
  bindValue(nameInput, profileForm.fields.name);
  bindValue(bankHintInput, profileForm.fields.bank_hint);
  bindSelect(signConventionSelect, profileForm.fields.sign_convention);
  bindCheckbox(invertAmountInput, profileForm.fields.invert_amount);
  bindValue(colDateInput, profileForm.fields.column_date);
  bindValue(colDescriptionInput, profileForm.fields.column_description);
  bindValue(colReferenceInput, profileForm.fields.column_reference);
  bindValue(colBalanceInput, profileForm.fields.column_balance);
  bindValue(colAmountInput, profileForm.fields.column_amount);
  bindValue(colDebitInput, profileForm.fields.column_debit);
  bindValue(colCreditInput, profileForm.fields.column_credit);
  bindValue(colTypeInput, profileForm.fields.column_type);
  bindValue(colDebitValuesInput, profileForm.fields.column_debit_values);
  bindValue(dateFormatInput, profileForm.fields.date_format);
  bindSelect(decimalStyleSelect, profileForm.fields.decimal_style);
  bindValue(headerRowsInput, profileForm.fields.header_rows);
  bindShow(amountRow, showAmountField);
  bindShow(separateRow, showSeparateFields);
  bindShow(typeRow, showTypeFields);
  deleteBtn.addEventListener("click", () => {
    void doDelete();
  });

  bindValue(previewSampleInput, profilePreviewSample);
  bindErrorText(previewErrorEl, profilePreviewError);
  previewBtn.addEventListener("click", () => {
    void previewProfileMapping();
  });

  list(
    previewBody,
    computed(() => profilePreviewRows.value ?? []),
    (r) => String(r.row_number),
    (r) => {
      const row = html`<tr class="${r.errors.length ? "preview-row-error" : ""}">
        <td>${r.date ?? "—"}</td>
        <td>${r.description}</td>
        <td>${r.amount ? formatMoney(r.amount) : "—"}</td>
        <td>${r.balance ? formatMoney(r.balance) : "—"}</td>
        <td>${r.reference ?? "—"}</td>
        <td>${r.errors.length ? r.errors.join("; ") : "OK"}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("profile preview row template produced no element");
      return labelRow(row, previewTable);
    },
  );

  return fragment;
}
